import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, CalendarDays, ShoppingCart, TrendingUp, Users } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../lib/api';
import { formatPrice } from '../../lib/mockData';
import { normalizeOrder, normalizeReservation } from '../../lib/backendAdapters';
import { useAuthStore } from '../../store/authStore';

const AdminReports: React.FC = () => {
    const { user } = useAuthStore();
    const [metrics, setMetrics] = useState<any>(null);
    const [orders, setOrders] = useState<any[]>([]);
    const [reservations, setReservations] = useState<any[]>([]);
    const [customers, setCustomers] = useState<any[]>([]);

    useEffect(() => {
        if (!user?.establishmentId) return;

        Promise.all([
            api.get(`/dashboard/metrics/${user.establishmentId}`),
            api.get(`/orders/establishment/${user.establishmentId}`),
            api.get(`/reservations/establishment/${user.establishmentId}`),
            api.get(`/customers/establishment/${user.establishmentId}`),
        ])
            .then(([metricsRes, ordersRes, reservationsRes, customersRes]) => {
                setMetrics(metricsRes.data);
                setOrders(ordersRes.data.map(normalizeOrder));
                setReservations(reservationsRes.data.map(normalizeReservation));
                setCustomers(customersRes.data);
            })
            .catch((error) => console.error('Failed to load reports', error));
    }, [user]);

    const avgTicket = useMemo(() => {
        if (!orders.length) return 0;
        return orders.reduce((sum, order) => sum + order.total, 0) / orders.length;
    }, [orders]);

    const orderMix = useMemo(() => ([
        { name: 'Mesa', total: orders.filter((order) => order.type === 'table').length },
        { name: 'Delivery', total: orders.filter((order) => order.type === 'delivery').length },
        { name: 'Balcão', total: orders.filter((order) => order.type === 'counter').length },
    ]), [orders]);

    if (!metrics) {
        return <div className="text-center py-12 text-muted-foreground">Carregando relatórios...</div>;
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-white">Relatórios</h1>
                <p className="text-muted-foreground text-sm mt-1">Visão consolidada da operação e desempenho</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div className="card p-5">
                    <ShoppingCart className="w-5 h-5 text-primary mb-3" />
                    <p className="text-sm text-muted-foreground">Pedidos</p>
                    <p className="text-2xl font-bold text-white mt-1">{orders.length}</p>
                </div>
                <div className="card p-5">
                    <TrendingUp className="w-5 h-5 text-success mb-3" />
                    <p className="text-sm text-muted-foreground">Faturamento hoje</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatPrice(metrics.todayRevenue)}</p>
                </div>
                <div className="card p-5">
                    <BarChart3 className="w-5 h-5 text-warning mb-3" />
                    <p className="text-sm text-muted-foreground">Ticket médio</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatPrice(avgTicket)}</p>
                </div>
                <div className="card p-5">
                    <Users className="w-5 h-5 text-blue-400 mb-3" />
                    <p className="text-sm text-muted-foreground">Clientes</p>
                    <p className="text-2xl font-bold text-white mt-1">{customers.length}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="card p-5">
                    <h2 className="section-title mb-4">Receita semanal</h2>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={metrics.weeklyRevenue}>
                            <CartesianGrid stroke="#2A2A2A" vertical={false} />
                            <XAxis dataKey="day" stroke="#64748B" />
                            <YAxis stroke="#64748B" />
                            <Tooltip
                                contentStyle={{ background: '#111', border: '1px solid #2A2A2A', borderRadius: 12 }}
                                formatter={(value) => [formatPrice(Number(value ?? 0)), 'Receita']}
                            />
                            <Bar dataKey="revenue" fill="#F97316" radius={[10, 10, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div className="card p-5">
                    <h2 className="section-title mb-4">Mix de pedidos</h2>
                    <div className="space-y-4">
                        {orderMix.map((entry) => (
                            <div key={entry.name} className="space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-white">{entry.name}</span>
                                    <span className="text-muted-foreground">{entry.total}</span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-[#1A1A1A] overflow-hidden">
                                    <div
                                        className="h-full bg-primary rounded-full"
                                        style={{ width: `${orders.length ? (entry.total / orders.length) * 100 : 0}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="card p-5">
                    <h2 className="section-title mb-4">Produtos mais vendidos</h2>
                    <div className="space-y-3">
                        {metrics.topProducts.map((product: any) => (
                            <div key={product.name} className="flex items-center justify-between py-2 border-b border-[#2A2A2A] last:border-0">
                                <span className="text-white">{product.name}</span>
                                <span className="badge badge-primary">{product.count}x</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="card p-5">
                    <h2 className="section-title mb-4">Reservas futuras</h2>
                    <div className="space-y-3">
                        {reservations.slice(0, 5).map((reservation) => (
                            <div key={reservation.id} className="flex items-center justify-between py-2 border-b border-[#2A2A2A] last:border-0">
                                <div>
                                    <p className="text-white">{reservation.customerName}</p>
                                    <p className="text-xs text-muted-foreground">{reservation.time}</p>
                                </div>
                                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                    <CalendarDays className="w-4 h-4" />
                                    {reservation.partySize} pessoas
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminReports;
